// Excel-import modal for the crew schedule. Moved verbatim from
// ManpowerPage.tsx (parse helpers included).

import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { manpowerApi, type BulkImportEntry } from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { DAY_ABBR, addDays, localYmd } from './lib/gridDates';
import { crewFillStyle, TH, TD, STATUS_CHIP } from './lib/crewStyles';
import { normalizeName } from './lib/scheduleModel';
import { shouldCloseAfterImport } from './lib/importResult';

// ─── Excel import helpers ─────────────────────────────────────────────────────

/** Convert Excel 1900-system date serial to YYYY-MM-DD */
function excelSerialToYmd(serial: number): string {
  const d = new Date(Date.UTC(1900, 0, 1) + (serial - 2) * 86_400_000);
  return d.toISOString().slice(0, 10);
}

interface ExcelWeek {
  weekStart: string; // YYYY-MM-DD (Monday of this week)
  label: string;     // "Mon May 11 – Sat May 16"
  colIndices: Array<{ colIndex: number; dateStr: string }>;
}

// Rows we should never try to import — section headers, dividers, totals.
// Recognized by their name; column E job# is also blank for these.
const NON_PROJECT_ROW_NAMES = new Set([
  'industrial total', 'gc total', 'commercial total', 'systac subtotal',
  'total manpower', 'total job and systac',
  'fab', 'scheduled off (both divisions)', 'gc', 'commercial', 'industrial',
  'rodbusters', 'flatwork crew',
]);

/** A single project row pulled from the spreadsheet. */
interface ExcelProject {
  rowIndex: number;                  // 0-based row in the source sheet (for stable keys)
  jobNumber: string | null;          // column E ("#")
  projectName: string;               // column G ("Job Name") — required
  divisionLabel: string | null;      // column A ("DIV") — IND / GC / Comm / …
  crewByCol: Map<number, number>;    // colIndex → crewCount, only populated days
}

interface ParsedExcel {
  weeks: ExcelWeek[];
  projects: ExcelProject[];
  colDateMap: Map<number, string>;
}

function parseExcelBuffer(buffer: ArrayBuffer): ParsedExcel {
  const wb = XLSX.read(buffer, { type: 'array' });
  // Use the last (most recent) sheet
  const sheetName = wb.SheetNames[wb.SheetNames.length - 1];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as (unknown[])[];

  // Row 0 = date serials; find all date columns
  const dateRow = rows[0] ?? [];
  const colDateMap = new Map<number, string>();

  (dateRow as unknown[]).forEach((val, i) => {
    if (typeof val === 'number' && val > 40_000) {
      colDateMap.set(i, excelSerialToYmd(val));
    }
  });

  // Group date columns by Mon–Sun week (week start = the Monday of that date)
  const weekMap = new Map<string, Array<{ colIndex: number; dateStr: string }>>();
  colDateMap.forEach((dateStr, colIndex) => {
    const d = new Date(dateStr + 'T12:00:00'); // noon UTC to avoid DST edge
    const dayOfWeek = d.getDay(); // 0=Sun
    const monday = addDays(d, dayOfWeek === 0 ? -6 : -(dayOfWeek - 1));
    const weekKey = localYmd(monday);
    if (!weekMap.has(weekKey)) weekMap.set(weekKey, []);
    weekMap.get(weekKey)!.push({ colIndex, dateStr });
  });

  // Build sorted weeks array
  const weeks: ExcelWeek[] = [...weekMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, cols]) => {
      const dates = cols.map((c) => c.dateStr).sort();
      const first = new Date(dates[0] + 'T12:00:00');
      const last  = new Date(dates[dates.length - 1] + 'T12:00:00');
      const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return {
        weekStart,
        label: `${fmt(first)} – ${fmt(last)}`,
        colIndices: cols.sort((a, b) => a.dateStr.localeCompare(b.dateStr)),
      };
    });

  // Parse project rows: skip row 0 (dates) and row 1 (column headers).
  // Accept rows that have EITHER a job number in column E OR a name in column G.
  // Skip known section-header / totals rows by name.
  const projects: ExcelProject[] = [];
  for (let r = 2; r < rows.length; r++) {
    const row = rows[r] as unknown[];
    if (!row) continue;

    const rawJob  = row[4];
    const rawName = row[6];
    const rawDiv  = row[0];

    const jobNumber = rawJob != null ? String(rawJob).trim() : '';
    const projectName = rawName != null ? String(rawName).trim() : '';

    if (!jobNumber && !projectName) continue;
    if (jobNumber === '#') continue;                                          // header echo
    if (NON_PROJECT_ROW_NAMES.has(projectName.toLowerCase())) continue;       // totals / dividers

    // Gather crew counts across every date column on this row
    const crewByCol = new Map<number, number>();
    colDateMap.forEach((_dateStr, colIndex) => {
      const val = row[colIndex];
      if (typeof val === 'number' && val > 0) crewByCol.set(colIndex, val);
    });
    if (crewByCol.size === 0) continue;                                       // no crew data

    projects.push({
      rowIndex: r,
      jobNumber: jobNumber || null,
      projectName,
      divisionLabel: rawDiv != null ? String(rawDiv).trim() : null,
      crewByCol,
    });
  }

  return { weeks, projects, colDateMap };
}

// ─── Import Modal ─────────────────────────────────────────────────────────────

interface ImportModalProps {
  onClose: () => void;
  onImported: () => void;
}

type ResolutionStatus = 'jobMatch' | 'nameMatch' | 'manualMatch' | 'unmatched';

type MatchedProject = { id: number; jobNumber: string; title: string };

interface ProjectResolution {
  status: ResolutionStatus;
  matchedProject?: MatchedProject;
}

/**
 * Token-overlap similarity between an Excel project name and a system project
 * title (0..1). Tokens are normalized (case/punctuation) and singularized so
 * "Locker Rooms- Block" still scores against "Locker Room and Parking
 * Expansion". Used to suggest manual links for rows with no job number.
 */
function nameSimilarity(a: string, b: string): number {
  const tokens = (s: string) => new Set(
    normalizeName(s).split(' ').filter((t) => t.length > 2).map((t) => t.replace(/s$/, '')),
  );
  const ta = tokens(a);
  const tb = tokens(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared++;
  return shared / Math.min(ta.size, tb.size);
}

export default function ImportModal({ onClose, onImported }: ImportModalProps) {
  const [parsed, setParsed]         = useState<ParsedExcel | null>(null);
  const [selectedWeeks, setSelectedWeeks] = useState<Set<string>>(() => new Set());
  const [dragging, setDragging]     = useState(false);
  const [fileName, setFileName]     = useState('');
  // Rows the user explicitly detached from their auto name-match (force placeholder)
  const [detachedRows, setDetachedRows] = useState<Set<number>>(() => new Set());
  // Rows the user manually linked to an existing project (rowIndex → project).
  // Covers rows with no job number whose Excel name is close-but-not-equal to
  // the real project title, which would otherwise import as placeholders.
  const [manualMatches, setManualMatches] = useState<Map<number, MatchedProject>>(() => new Map());
  // Per-row text typed into the "search to link" box on unmatched rows.
  const [linkSearch, setLinkSearch] = useState<Record<number, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  // Existing projects — used for case-insensitive name matching in the preview.
  // Must be the unscoped manpower list: projectsApi.list only returns the
  // caller's own assignments, which made every Excel row preview as unmatched.
  const { data: existingProjects = [] } = useQuery<
    { id: number; jobNumber: string; title: string }[]
  >({
    queryKey: ['manpower-project-options', { includeArchived: true }],
    queryFn: () => manpowerApi.projectOptions(true),
    staleTime: 60_000,
    select: (d: any[]) =>
      d.map((p: any) => ({ id: p.id, jobNumber: p.jobNumber, title: p.title })),
  });

  // Build fast lookups: by lowercased job# and by normalized title.
  const projectsByJob  = useMemo(() => {
    const m = new Map<string, { id: number; jobNumber: string; title: string }>();
    for (const p of existingProjects) m.set(p.jobNumber.trim().toLowerCase(), p);
    return m;
  }, [existingProjects]);
  const projectsByName = useMemo(() => {
    const m = new Map<string, { id: number; jobNumber: string; title: string }>();
    for (const p of existingProjects) m.set(normalizeName(p.title), p);
    return m;
  }, [existingProjects]);

  const importMutation = useMutation({
    mutationFn: (entries: BulkImportEntry[]) => manpowerApi.bulkImport(entries),
    // ⚠️ Only close when there is nothing to tell them (#478). The success
    // summary below — and the warnings, which were not rendered at all — used
    // to be unreachable: onSuccess closed the modal on the same tick, so the
    // component unmounted before either could paint. Every warning the API has
    // ever produced went straight in the bin, including "rows were not imported
    // because that job was deleted", which is a partial result presented as a
    // clean success. That is the same silent-success defect this issue is about,
    // one layer up.
    //
    // The grid still refreshes either way; it is only the closing that waits.
    onSuccess: (data: any) => {
      onImported();
      if (shouldCloseAfterImport(data)) onClose();
    },
  });

  function handleFile(file: File) {
    if (!file.name.match(/\.xlsx?$/i)) return;
    setFileName(file.name);
    setDetachedRows(new Set());
    setManualMatches(new Map());
    setLinkSearch({});
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buf = e.target?.result as ArrayBuffer;
        const result = parseExcelBuffer(buf);
        setParsed(result);
        // Auto-select most recent week
        if (result.weeks.length > 0) {
          setSelectedWeeks(new Set([result.weeks[result.weeks.length - 1].weekStart]));
        } else {
          setSelectedWeeks(new Set());
        }
      } catch (err) {
        console.error('Excel parse error:', err);
        alert('Could not parse the file. Please ensure it is a valid manpower schedule.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function toggleWeek(weekStart: string) {
    setSelectedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekStart)) next.delete(weekStart);
      else next.add(weekStart);
      return next;
    });
  }

  function selectAllWeeks() {
    if (!parsed) return;
    setSelectedWeeks(new Set(parsed.weeks.map((w) => w.weekStart)));
  }

  function clearWeeks() {
    setSelectedWeeks(new Set());
  }

  function toggleDetached(rowIndex: number) {
    setDetachedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex); else next.add(rowIndex);
      return next;
    });
  }

  // Weeks chosen by the user, sorted chronologically
  const activeWeeks = useMemo(() => {
    if (!parsed) return [] as ExcelWeek[];
    return parsed.weeks.filter((w) => selectedWeeks.has(w.weekStart));
  }, [parsed, selectedWeeks]);

  // Flat list of {colIndex, dateStr, weekStart} for every selected day, in date order
  const activeColumns = useMemo(() => {
    return activeWeeks.flatMap((w) =>
      w.colIndices.map((c) => ({ ...c, weekStart: w.weekStart }))
    );
  }, [activeWeeks]);

  // Resolve every parsed Excel project against existing system projects.
  const resolutions = useMemo<Map<number, ProjectResolution>>(() => {
    const map = new Map<number, ProjectResolution>();
    if (!parsed) return map;
    for (const ep of parsed.projects) {
      // 1. Job# match
      if (ep.jobNumber) {
        const byJob = projectsByJob.get(ep.jobNumber.toLowerCase());
        if (byJob) {
          map.set(ep.rowIndex, { status: 'jobMatch', matchedProject: byJob });
          continue;
        }
      }
      // 2. Manual link picked by the user in the preview
      const manual = manualMatches.get(ep.rowIndex);
      if (manual) {
        map.set(ep.rowIndex, { status: 'manualMatch', matchedProject: manual });
        continue;
      }
      // 3. Name match (unless user detached it)
      if (!detachedRows.has(ep.rowIndex)) {
        const byName = projectsByName.get(normalizeName(ep.projectName));
        if (byName) {
          map.set(ep.rowIndex, { status: 'nameMatch', matchedProject: byName });
          continue;
        }
      }
      // 4. Unmatched — will create placeholder
      map.set(ep.rowIndex, { status: 'unmatched' });
    }
    return map;
  }, [parsed, projectsByJob, projectsByName, detachedRows, manualMatches]);

  // Fuzzy link suggestions for rows that didn't auto-match: the top few
  // projects whose titles share most of the Excel name's tokens.
  const suggestionsByRow = useMemo(() => {
    const m = new Map<number, MatchedProject[]>();
    if (!parsed) return m;
    for (const ep of parsed.projects) {
      if (resolutions.get(ep.rowIndex)?.status !== 'unmatched') continue;
      const cands = existingProjects
        .map((p) => ({ p, score: nameSimilarity(ep.projectName, p.title) }))
        .filter((x) => x.score >= 0.5)
        .sort((x, y) => y.score - x.score)
        .slice(0, 3)
        .map((x) => x.p);
      if (cands.length > 0) m.set(ep.rowIndex, cands);
    }
    return m;
  }, [parsed, existingProjects, resolutions]);

  function linkRow(rowIndex: number, p: MatchedProject) {
    setManualMatches((prev) => new Map(prev).set(rowIndex, p));
    setLinkSearch((prev) => ({ ...prev, [rowIndex]: '' }));
  }

  function unlinkRow(rowIndex: number) {
    setManualMatches((prev) => {
      const next = new Map(prev);
      next.delete(rowIndex);
      return next;
    });
  }

  // Build preview entries across all selected weeks. We always send projectName +
  // divisionLabel so the backend can name-match or create placeholders.
  const previewEntries = useMemo<BulkImportEntry[]>(() => {
    if (!parsed || activeWeeks.length === 0) return [];

    const entries: BulkImportEntry[] = [];
    for (const ep of parsed.projects) {
      const res = resolutions.get(ep.rowIndex);
      // If user detached the name match, suppress the job number too so the
      // backend treats the row as truly unmatched and creates a placeholder.
      const sendJobNumber =
        res?.status === 'jobMatch'
          ? (ep.jobNumber ?? undefined)
          : res?.status === 'nameMatch' || res?.status === 'manualMatch'
            ? (res.matchedProject?.jobNumber)
            : undefined;

      for (const w of activeWeeks) {
        for (const { colIndex, dateStr } of w.colIndices) {
          const crewCount = ep.crewByCol.get(colIndex) ?? 0;
          if (crewCount > 0) {
            entries.push({
              jobNumber:     sendJobNumber || undefined,
              projectName:   ep.projectName,
              divisionLabel: ep.divisionLabel ?? undefined,
              workDate:      dateStr,
              crewCount,
            });
          }
        }
      }
    }
    return entries;
  }, [parsed, activeWeeks, resolutions]);

  // For preview table: project rows (one per ExcelProject), with their crew-counts per active day.
  const previewRows = useMemo(() => {
    if (!parsed) return [];
    return parsed.projects
      .filter((ep) =>
        activeColumns.some(({ colIndex }) => (ep.crewByCol.get(colIndex) ?? 0) > 0),
      )
      .map((ep) => {
        const res = resolutions.get(ep.rowIndex)!;
        const dateMap: Record<string, number> = {};
        for (const { colIndex, dateStr } of activeColumns) {
          const v = ep.crewByCol.get(colIndex) ?? 0;
          if (v > 0) dateMap[dateStr] = v;
        }
        return { ep, res, dateMap };
      });
  }, [parsed, activeColumns, resolutions]);

  const counts = useMemo(() => {
    let jobMatch = 0, nameMatch = 0, manualMatch = 0, unmatched = 0;
    for (const r of previewRows) {
      if (r.res.status === 'jobMatch')        jobMatch++;
      else if (r.res.status === 'nameMatch')   nameMatch++;
      else if (r.res.status === 'manualMatch') manualMatch++;
      else                                     unmatched++;
    }
    return { jobMatch, nameMatch, manualMatch, unmatched, total: previewRows.length };
  }, [previewRows]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} style={{ background: 'var(--ew-white)', borderRadius: 'var(--ew-r-lg)', width: '100%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--ew-shadow-3)', fontFamily: 'var(--ew-font)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--ew-n-200)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Import Schedule from Excel</h3>
          <button onClick={onClose} aria-label="Close import modal" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--ew-n-400)', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? 'var(--ew-green)' : 'var(--ew-n-300)'}`,
              borderRadius: 'var(--ew-r-lg)',
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? 'var(--ew-green-50)' : 'var(--ew-n-50)',
              marginBottom: 20,
              transition: 'border-color 120ms, background 120ms',
            }}
          >
            <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            <div style={{ fontSize: 28, marginBottom: 6 }}>📊</div>
            {fileName ? (
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ew-green-700)' }}>{fileName}</div>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ew-dark)', marginBottom: 4 }}>Drop your manpower Excel file here</div>
                <div style={{ fontSize: 12, color: 'var(--ew-n-400)' }}>or click to browse · .xlsx / .xls</div>
              </>
            )}
          </div>

          {parsed && (
            <>
              {/* Week selector */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ew-dark)' }}>
                    Select weeks to import:
                  </label>
                  <button
                    type="button"
                    onClick={selectAllWeeks}
                    style={{ background: 'none', border: 'none', color: 'var(--ew-green-700)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearWeeks}
                    style={{ background: 'none', border: 'none', color: 'var(--ew-n-500)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    Clear
                  </button>
                  {previewEntries.length > 0 && (
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ew-n-500)', whiteSpace: 'nowrap' }}>
                      {activeWeeks.length} week{activeWeeks.length === 1 ? '' : 's'} · {counts.total} projects · {previewEntries.length} entries
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: 8, background: 'var(--ew-n-50)' }}>
                  {parsed.weeks.map((w) => {
                    const checked = selectedWeeks.has(w.weekStart);
                    return (
                      <label
                        key={w.weekStart}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          fontWeight: 600,
                          border: `1px solid ${checked ? 'var(--ew-green)' : 'var(--ew-n-300)'}`,
                          borderRadius: 'var(--ew-r-pill)',
                          background: checked ? 'var(--ew-green-50)' : 'var(--ew-white)',
                          color: checked ? 'var(--ew-green-700)' : 'var(--ew-dark)',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleWeek(w.weekStart)}
                          style={{ margin: 0 }}
                        />
                        {w.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Match-status summary chips */}
              {activeWeeks.length > 0 && previewRows.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', fontSize: 11 }}>
                  <span style={{ ...STATUS_CHIP, color: '#065f46', background: '#d1fae5', borderColor: '#a7f3d0' }}>
                    ✓ {counts.jobMatch} matched by Job#
                  </span>
                  <span style={{ ...STATUS_CHIP, color: '#1e40af', background: '#dbeafe', borderColor: '#bfdbfe' }}>
                    🔗 {counts.nameMatch} matched by Name
                  </span>
                  {counts.manualMatch > 0 && (
                    <span style={{ ...STATUS_CHIP, color: '#6b21a8', background: '#f3e8ff', borderColor: '#e9d5ff' }}>
                      🖇 {counts.manualMatch} linked manually
                    </span>
                  )}
                  {counts.unmatched > 0 && (
                    <span style={{ ...STATUS_CHIP, color: '#92400e', background: '#fef3c7', borderColor: '#fde68a' }}>
                      ⚠ {counts.unmatched} will create placeholder
                    </span>
                  )}
                </div>
              )}

              {/* Preview table */}
              {activeWeeks.length > 0 && previewRows.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', maxHeight: 360 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--ew-font)' }}>
                    <thead>
                      <tr>
                        <th style={{ ...TH, textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0, minWidth: 220 }}>Excel Project → Match</th>
                        {activeColumns.map(({ dateStr, weekStart }, i) => {
                          const d = new Date(dateStr + 'T12:00:00');
                          const prevWeek = i > 0 ? activeColumns[i - 1].weekStart : null;
                          const isWeekStart = prevWeek !== null && prevWeek !== weekStart;
                          return (
                            <th key={dateStr} style={{ ...TH, width: 52, position: 'sticky', top: 0, borderLeft: isWeekStart ? '2px solid rgba(255,255,255,0.25)' : undefined }}>
                              <div style={{ fontSize: 9, opacity: 0.65 }}>{DAY_ABBR[d.getDay()]}</div>
                              <div>{d.getMonth() + 1}/{d.getDate()}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map(({ ep, res, dateMap }) => {
                        const bg = res.status === 'unmatched' ? '#fffbeb'
                                 : res.status === 'nameMatch' ? '#eff6ff'
                                 : res.status === 'manualMatch' ? '#faf5ff'
                                 : 'var(--ew-white)';
                        const q = (linkSearch[ep.rowIndex] ?? '').trim();
                        const searchResults = q.length >= 2
                          ? existingProjects
                              .filter((p) => `${p.jobNumber} ${p.title}`.toLowerCase().includes(q.toLowerCase()))
                              .slice(0, 6)
                          : [];
                        return (
                          <tr key={ep.rowIndex} style={{ background: bg }}>
                            <td style={{ ...TD, textAlign: 'left', padding: '5px 10px', fontVariantNumeric: 'tabular-nums' }}>
                              <div style={{ fontWeight: 600, fontSize: 12 }}>
                                {ep.jobNumber && <span style={{ color: 'var(--ew-n-500)', marginRight: 6 }}>{ep.jobNumber}</span>}
                                {ep.projectName}
                              </div>
                              <div style={{ fontSize: 10, marginTop: 2 }}>
                                {res.status === 'jobMatch' && (
                                  <span style={{ color: '#065f46' }}>
                                    ✓ Matched by Job# → {res.matchedProject?.title}
                                  </span>
                                )}
                                {res.status === 'nameMatch' && (
                                  <span style={{ color: '#1e40af' }}>
                                    🔗 Matched by Name → {res.matchedProject?.jobNumber} · {res.matchedProject?.title}
                                    {' '}
                                    <button
                                      type="button"
                                      onClick={() => toggleDetached(ep.rowIndex)}
                                      title="Unlink — import as a placeholder instead"
                                      style={{ marginLeft: 6, background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: 10, textDecoration: 'underline', padding: 0 }}
                                    >
                                      not a match?
                                    </button>
                                  </span>
                                )}
                                {res.status === 'manualMatch' && (
                                  <span style={{ color: '#6b21a8' }}>
                                    🖇 Linked manually → {res.matchedProject?.jobNumber} · {res.matchedProject?.title}
                                    {' '}
                                    <button
                                      type="button"
                                      onClick={() => unlinkRow(ep.rowIndex)}
                                      title="Unlink — back to creating a placeholder"
                                      style={{ marginLeft: 6, background: 'none', border: 'none', color: '#6b21a8', cursor: 'pointer', fontSize: 10, textDecoration: 'underline', padding: 0 }}
                                    >
                                      unlink
                                    </button>
                                  </span>
                                )}
                                {res.status === 'unmatched' && (
                                  <span style={{ color: '#92400e' }}>
                                    ⚠ No match — will create placeholder ({ep.divisionLabel || 'no division'})
                                    {detachedRows.has(ep.rowIndex) && (
                                      <>
                                        {' '}
                                        <button
                                          type="button"
                                          onClick={() => toggleDetached(ep.rowIndex)}
                                          title="Re-apply name match"
                                          style={{ marginLeft: 6, background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: 10, textDecoration: 'underline', padding: 0 }}
                                        >
                                          undo unlink
                                        </button>
                                      </>
                                    )}
                                  </span>
                                )}
                              </div>
                              {/* Manual-link controls: fuzzy suggestions + project search */}
                              {res.status === 'unmatched' && (
                                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                  {(suggestionsByRow.get(ep.rowIndex) ?? []).map((p) => (
                                    <button
                                      key={p.id}
                                      type="button"
                                      onClick={() => linkRow(ep.rowIndex, p)}
                                      title={`Link this row to ${p.jobNumber} · ${p.title}`}
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, padding: '2px 8px', background: '#f3e8ff', color: '#6b21a8', border: '1px solid #e9d5ff', borderRadius: 'var(--ew-r-pill)', cursor: 'pointer', fontFamily: 'var(--ew-font)' }}
                                    >
                                      🔗 {p.jobNumber} · {p.title}
                                    </button>
                                  ))}
                                  <input
                                    value={linkSearch[ep.rowIndex] ?? ''}
                                    onChange={(e) => setLinkSearch((prev) => ({ ...prev, [ep.rowIndex]: e.target.value }))}
                                    placeholder="Search projects to link…"
                                    aria-label={`Search projects to link to ${ep.projectName}`}
                                    style={{ fontSize: 10, padding: '2px 8px', border: '1px solid var(--ew-n-300)', borderRadius: 'var(--ew-r-pill)', width: 160, fontFamily: 'var(--ew-font)' }}
                                  />
                                  {q.length >= 2 && (
                                    searchResults.length > 0 ? searchResults.map((p) => (
                                      <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => linkRow(ep.rowIndex, p)}
                                        title={`Link this row to ${p.jobNumber} · ${p.title}`}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, padding: '2px 8px', background: 'var(--ew-white)', color: 'var(--ew-n-500)', border: '1px solid var(--ew-n-300)', borderRadius: 'var(--ew-r-pill)', cursor: 'pointer', fontFamily: 'var(--ew-font)' }}
                                      >
                                        {p.jobNumber} · {p.title}
                                      </button>
                                    )) : (
                                      <span style={{ fontSize: 10, color: 'var(--ew-n-400)' }}>No projects match "{q}"</span>
                                    )
                                  )}
                                </div>
                              )}
                            </td>
                            {activeColumns.map(({ dateStr, weekStart }, i) => {
                              const count = dateMap[dateStr] ?? 0;
                              const prevWeek = i > 0 ? activeColumns[i - 1].weekStart : null;
                              const isWeekStart = prevWeek !== null && prevWeek !== weekStart;
                              return (
                                <td key={dateStr} style={{ ...TD, padding: '5px 4px', borderLeft: isWeekStart ? '2px solid var(--ew-n-300)' : undefined, ...crewFillStyle(count) }}>
                                  {count > 0 ? count : ''}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {activeWeeks.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--ew-n-400)', fontSize: 13 }}>
                  Select at least one week to preview entries.
                </div>
              )}

              {activeWeeks.length > 0 && previewRows.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--ew-n-400)', fontSize: 13 }}>
                  No crew entries found for the selected week{activeWeeks.length === 1 ? '' : 's'}.
                </div>
              )}
            </>
          )}

          {/* Error state */}
          {importMutation.isError && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#FFF5F5', border: '1px solid #FED7D7', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: 'var(--ew-danger)' }}>
              Import failed: {(importMutation.error as Error).message}
            </div>
          )}

          {/* Success summary */}
          {importMutation.isSuccess && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--ew-green-50)', border: '1px solid var(--ew-green-100)', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: 'var(--ew-green-700)' }}>
              ✓ Imported {importMutation.data?.imported} entries
              {(importMutation.data?.matchedByName ?? 0) > 0 && ` · ${importMutation.data?.matchedByName} matched by name`}
              {(importMutation.data?.autoCreated ?? 0) > 0 && ` · ${importMutation.data?.autoCreated} placeholder project${importMutation.data?.autoCreated === 1 ? '' : 's'} created`}
              {(importMutation.data?.skipped ?? 0) > 0 && ` · ${importMutation.data?.skipped} skipped`}
            </div>
          )}

          {/* Warnings — rendered separately from the green summary and in
              amber, because "imported 402 entries" and "3 rows were dropped"
              are different news and the second should not read as part of a
              tick.
              ⚠️ The heading depends on droppedRows, not on the warnings being
              present: most warnings are the routine "created placeholder"
              note, where everything DID import and calling it "not everything
              was imported" would be a lie. */}
          {importMutation.isSuccess && (importMutation.data?.warnings?.length ?? 0) > 0 && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#FFFAEB', border: '1px solid #FDE68A', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: '#92400E' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {(importMutation.data?.droppedRows ?? 0) > 0
                  ? `Not everything was imported — ${importMutation.data?.droppedRows} row${importMutation.data?.droppedRows === 1 ? '' : 's'} did not land`
                  : 'Worth a look'}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {importMutation.data!.warnings.map((w: string, i: number) => (
                  <li key={i} style={{ marginBottom: 2 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        {/* Once the import has run, the work is done and the modal is only
            still open to report it (#478) — so "Cancel" is the wrong word, and
            leaving "Import N entries" live invites importing the same file
            twice on a screen whose whole message is "some rows did not land". */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px', borderTop: '1px solid var(--ew-n-200)' }}>
          {importMutation.isSuccess ? (
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={previewEntries.length === 0 || importMutation.isPending}
                onClick={() => importMutation.mutate(previewEntries)}
              >
                {importMutation.isPending ? 'Importing…' : `Import ${previewEntries.length} entries`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
